import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import ChangeLine from '../../components/novarium/ChangeLine';
import InfoTip from '../../components/novarium/InfoTip';
import { formatShortDate, periodNeedsYear } from '../../components/novarium/periodRange';
import { NewBornFilterDTO, NewBornKpisDTO, NewBornSummaryDTO } from '../../dto/newborn/NewBornDTO';
import { percentVariation, pointsVariation, Variation, Verdict, waitVerdict } from '../ServicesStatistics/statisticsChange';
import { parseIsoDate } from '../ServicesStatistics/statisticsUtils';
import { formatCount, formatPercent } from '../UrgencySummary/urgencySummaryFormat';
import { NewBornSelectFilters } from './newBornFilterModel';

interface IndicatorProps {
    label: string;
    value: string;
    /** Frase corta que explica el indicador (ⓘ). */
    hint?: string;
    /** Renglones de detalle: «X de N» y la variación contra el período anterior. */
    details?: ReactNode[];
}

function Indicator({ label, value, hint, details = [] }: IndicatorProps) {
    return (
        <div className="nv-card nv-newborn-ind">
            <dt className="nv-newborn-ind__label">
                {label}
                {hint && <InfoTip text={hint} />}
            </dt>
            <dd className="nv-kpi">{value}</dd>
            {details.length === 0 && <dd className="nv-newborn-ind__detail">{' '}</dd>}
            {details.map((detail, index) => <dd key={index} className="nv-newborn-ind__detail">{detail}</dd>)}
        </div>
    );
}

/** Un porcentaje (en %) como fracción, que es lo que compara `pointsVariation`. */
const toRatio = (percent: number | null): number | null => (percent === null ? null : percent / 100);

/** "1/8–31/8": el período anterior con que se compara; `null` si el backend no lo manda. */
function previousPeriodText(summary: NewBornSummaryDTO): string | null {
    if (!summary.previousFrom || !summary.previousTo) return null;
    const from = parseIsoDate(summary.previousFrom);
    const to = parseIsoDate(summary.previousTo);
    const withYear = periodNeedsYear(from, to, new Date());
    return `${formatShortDate(from, withYear)}–${formatShortDate(to, withYear)}`;
}

interface NewBornIndicatorsProps {
    summary: NewBornSummaryDTO;
    /** Los filtros aplicados: la nota de nacidos muertos o a revisar no se ofrece si ya se está filtrando por eso. */
    applied: NewBornFilterDTO;
    /** Aplica un filtro de «Más filtros» (el enlace de las notas). */
    onFilter: (patch: Partial<NewBornSelectFilters>) => void;
}

/**
 * Los cuatro indicadores del período: nacimientos (neutro, sin veredicto), % de cesáreas, prematuros y bajo peso, cada uno
 * con su variación contra el período anterior de igual largo, escrita con las fechas de ese período. En los tres últimos,
 * subir es peor y bajar es mejor, pero «mejor» y «peor» solo se escriben si las dos bases tienen el mínimo de casos que
 * manda el backend: con unos 60 nacimientos por mes, uno o dos casos mueven el porcentaje. Sin meta fija: la fija la
 * dirección médica. Los nacidos muertos y los registros a revisar no entran en los indicadores clínicos y se informan aparte.
 */
export default function NewBornIndicators({ summary, applied, onFilter }: NewBornIndicatorsProps) {
    const intl = useIntl();
    const { current, previous, minCasesForVerdict } = summary;
    const period = previousPeriodText(summary);

    const line = (variation: Variation | null, verdict?: Verdict): ReactNode =>
        (variation && period ? <ChangeLine variation={variation} period={period} verdict={verdict} /> : undefined);

    /** Una tasa (%): variación en puntos y, con el mínimo de casos en las dos bases, veredicto (subir es peor). */
    const rate = (
        pct: (kpis: NewBornKpisDTO) => number | null,
        base: (kpis: NewBornKpisDTO) => number,
    ): ReactNode => {
        if (!previous) return undefined;
        const variation = pointsVariation(intl, toRatio(pct(current)), toRatio(pct(previous)));
        const enough = base(current) >= minCasesForVerdict && base(previous) >= minCasesForVerdict;
        return line(variation, variation && enough ? waitVerdict(variation.trend) : null);
    };

    const ofTotal = (count: number, base: number, messageId: string): string =>
        intl.formatMessage({ id: messageId }, { count: formatCount(intl, count), total: formatCount(intl, base) });

    const limit = formatCount(intl, minCasesForVerdict);

    return (
        <div className="nv-newborn-indicators-box">
            <dl className="nv-newborn-indicators" aria-label={intl.formatMessage({ id: 'NewBornKpisLabel' })}>
                <Indicator
                    label={intl.formatMessage({ id: 'NewBornKpiBirths' })}
                    hint={intl.formatMessage({ id: 'NewBornKpiBirthsHint' })}
                    value={formatCount(intl, current.births)}
                    details={[previous ? line(percentVariation(current.births, previous.births)) : undefined]}
                />
                <Indicator
                    label={intl.formatMessage({ id: 'NewBornKpiCesarean' })}
                    hint={intl.formatMessage({ id: 'NewBornKpiCesareanHint' }, { min: limit })}
                    value={formatPercent(intl, current.cesareanPct)}
                    details={[
                        current.cesareanBase > 0 ? ofTotal(current.cesareans, current.cesareanBase, 'NewBornKpiOfDeliveries') : undefined,
                        rate((kpis) => kpis.cesareanPct, (kpis) => kpis.cesareanBase),
                    ]}
                />
                <Indicator
                    label={intl.formatMessage({ id: 'NewBornKpiPremature' })}
                    hint={intl.formatMessage({ id: 'NewBornKpiPrematureHint' }, { min: limit })}
                    value={formatPercent(intl, current.pretermPct)}
                    details={[
                        current.pretermBase > 0 ? ofTotal(current.preterm, current.pretermBase, 'NewBornKpiOf') : undefined,
                        rate((kpis) => kpis.pretermPct, (kpis) => kpis.pretermBase),
                    ]}
                />
                <Indicator
                    label={intl.formatMessage({ id: 'NewBornKpiLowWeight' })}
                    hint={intl.formatMessage({ id: 'NewBornKpiLowWeightHint' }, { min: limit })}
                    value={formatPercent(intl, current.lowWeightPct)}
                    details={[
                        current.lowWeightBase > 0 ? ofTotal(current.lowWeight, current.lowWeightBase, 'NewBornKpiOf') : undefined,
                        rate((kpis) => kpis.lowWeightPct, (kpis) => kpis.lowWeightBase),
                    ]}
                />
            </dl>

            {current.stillbirths > 0 && applied.birthState !== 'STILLBORN' && (
                <Note
                    count={current.stillbirths}
                    labelId="NewBornNoteStillborn"
                    actionId="NewBornNoteStillbornAction"
                    hintId="NewBornNoteStillbornHint"
                    onApply={() => onFilter({ birthState: 'STILLBORN' })}
                />
            )}
            {current.needsReview > 0 && applied.needsReview !== true && (
                <Note
                    count={current.needsReview}
                    labelId="NewBornNoteReview"
                    actionId="NewBornNoteReviewAction"
                    hintId="NewBornNoteReviewHint"
                    onApply={() => onFilter({ needsReview: true })}
                />
            )}
        </div>
    );
}

interface NoteProps {
    count: number;
    labelId: string;
    actionId: string;
    hintId: string;
    onApply: () => void;
}

/**
 * «N nacidos muertos» / «N registros a revisar»: lo que no entra en los indicadores clínicos. El enlace filtra la grilla por
 * eso y el criterio se lee en el ⓘ. Solo se muestra con N > 0.
 */
function Note({ count, labelId, actionId, hintId, onApply }: NoteProps) {
    const intl = useIntl();
    const countText = formatCount(intl, count);
    return (
        <p className="nv-note" role="status">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <Button
                type="button"
                link
                label={intl.formatMessage({ id: labelId }, { count, countText })}
                aria-label={intl.formatMessage({ id: actionId }, { count, countText })}
                onClick={onApply}
            />
            <InfoTip text={intl.formatMessage({ id: hintId })} />
        </p>
    );
}
